import type { PartnerCard } from '@qlk/shared';
import { Link } from 'react-router-dom';

import { StatusBadge, dinhDangTien } from '../../../ui/index.js';

/** Hai chữ cái đầu làm logo, giống ô avatar của kênh. */
function chuVietTat(ten: string): string {
  const tu = ten
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (tu.length === 0) return '??';
  if (tu.length === 1) return (tu[0] ?? '').slice(0, 2).toUpperCase();
  return `${(tu[0] ?? '').charAt(0)}${(tu[1] ?? '').charAt(0)}`.toUpperCase();
}

const NHAN_TRANG_THAI: Record<
  PartnerCard['state'],
  { text: string; tone: 'ok' | 'info' | 'neutral' | 'warn' }
> = {
  paying: { text: 'Đang trả tiền', tone: 'ok' },
  waiting: { text: 'Chờ kênh kiếm tiền', tone: 'info' },
  no_channel: { text: 'Chưa gán kênh', tone: 'neutral' },
  adsense_pending: { text: 'Chờ liên kết AdSense', tone: 'warn' },
  adsense_linked: { text: 'Đã liên kết AdSense', tone: 'ok' },
};

export interface TheDoiTacProps {
  doiTac: PartnerCard;
  /** Nhãn cột tiền, ví dụ `Gộp · Tháng 9/2026`. */
  nhanKy: string;
  /**
   * Doanh thu gộp **của kỳ đang chọn** (`GET /networks/tien-ky`).
   *
   * Bỏ qua thì dùng `doiTac.gross` — số của kỳ mà `GET /networks` nhận được.
   * Trang Network truyền vào để thẻ và ba ô chỉ số nói về **cùng một kỳ**.
   */
  gross?: string | null;
  /** Số của kỳ chưa chốt theo tháng; bỏ qua thì theo `doiTac.tamTinh`. */
  tamTinh?: boolean;
}

export function TheDoiTac({ doiTac, nhanKy, gross, tamTinh }: TheDoiTacProps) {
  const tien = gross === undefined ? doiTac.gross : gross;
  const laTamTinh = tamTinh ?? doiTac.tamTinh;
  const trangThai = NHAN_TRANG_THAI[doiTac.state];
  const laAdsense = doiTac.kind === 'adsense_direct';

  return (
    <Link
      to={`/network/${doiTac.id}`}
      className={`block rounded-the border bg-be-mat p-6 transition-shadow hover:shadow-[0_4px_12px_rgba(16,24,40,0.08)] ${
        laAdsense ? 'border-warn-chu/40' : 'border-vien'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className="so-lieu inline-flex size-10 shrink-0 items-center justify-center rounded-[10px] text-phu font-bold text-white"
          style={{ backgroundColor: doiTac.logoColor }}
          aria-hidden="true"
        >
          {chuVietTat(doiTac.name)}
        </span>
        <div className="min-w-0">
          <p className="truncate font-semibold text-chu">{doiTac.name}</p>
          <p className="text-phu text-chu-mo">
            {laAdsense
              ? 'Trực tiếp, không qua Network'
              : `Network${doiTac.category === 'music' ? ' · nhạc' : ''}`}
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-end justify-between gap-3">
        <div>
          <p className="text-phu text-chu-mo">
            {nhanKy}
            {/*
             * Kỳ chưa có sao kê thì số là tạm tính từ YouTube API — ghi rõ,
             * đừng để người đọc tưởng đã đối soát.
             */}
            {laTamTinh && <span className="ml-1 text-warn-chu">· tạm tính</span>}
          </p>
          <p className="so-lieu mt-1 text-so-lon font-bold text-chu">
            {tien === null || Number(tien) === 0 ? '—' : dinhDangTien(tien)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-phu text-chu-mo">Kênh</p>
          <p className="so-lieu mt-1 text-so-lon font-bold text-chu">{doiTac.channelCount}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <StatusBadge tone={trangThai.tone}>{trangThai.text}</StatusBadge>
        {/*
         * Link đang hoạt động mà chưa gắn hợp đồng là số tiền đang sai: tỷ lệ
         * Network về 0 % và thuế Mỹ rơi về mặc định 30 %. Ca thật 16/10/2026:
         * BerlinTomek có 2 kênh nhưng tab Hợp đồng chỉ đếm 1, và không màn nào
         * nói ra điều đó.
         */}
        {doiTac.soLinkChuaGanHopDong > 0 && (
          <StatusBadge tone="warn">
            {doiTac.soLinkChuaGanHopDong === 1
              ? '1 kênh chưa gắn hợp đồng'
              : `${String(doiTac.soLinkChuaGanHopDong)} kênh chưa gắn hợp đồng`}
          </StatusBadge>
        )}
      </div>
    </Link>
  );
}
